import { Link } from 'react-router-dom';
import '../styles/TermsOfService.css';

function TermsOfService() {
  return (
    <div className="terms-page-wrapper">
      {/* Animated Background Glows */}
      <div className="terms-bg-glow terms-bg-glow-1"></div>
      <div className="terms-bg-glow terms-bg-glow-2"></div>

      <div className="terms-container">
        {/* ============================= */}
        {/* HERO SECTION */}
        {/* ============================= */}
        <section className="terms-hero-section">
          <div className="terms-hero-content">
            <span className="terms-hero-badge">📋 Terms of Service</span>
            <h1 className="terms-hero-title">
              Our <span className="terms-hero-highlight">Terms</span> & Conditions
            </h1>
            <p className="terms-hero-description">
              By using VernaEvents, you agree to these terms. Please read them carefully 
              before using our platform to register for events or participate in tournaments.
            </p>
            <div className="terms-hero-meta">
              <span className="terms-hero-meta-item">📅 Last Updated: July 2026</span>
              <span className="terms-hero-meta-item">📋 Version 2.0</span>
            </div>
          </div>
        </section>

        {/* ============================= */}
        {/* TABLE OF CONTENTS */}
        {/* ============================= */}
        <section className="terms-toc-section">
          <div className="terms-toc-content">
            <h2>Table of Contents</h2>
            <div className="terms-toc-grid">
              <a href="#acceptance" className="terms-toc-item">
                <span>1.</span> Acceptance of Terms
              </a>
              <a href="#user-accounts" className="terms-toc-item">
                <span>2.</span> User Accounts
              </a>
              <a href="#event-registration" className="terms-toc-item">
                <span>3.</span> Event Registration
              </a>
              <a href="#payments" className="terms-toc-item">
                <span>4.</span> Payments & Fees
              </a>
              <a href="#cancellation" className="terms-toc-item">
                <span>5.</span> Cancellation Policy
              </a>
              <a href="#user-conduct" className="terms-toc-item">
                <span>6.</span> User Conduct
              </a>
              <a href="#intellectual-property" className="terms-toc-item">
                <span>7.</span> Intellectual Property
              </a>
              <a href="#disclaimer" className="terms-toc-item">
                <span>8.</span> Disclaimer of Warranties
              </a>
              <a href="#liability" className="terms-toc-item">
                <span>9.</span> Limitation of Liability
              </a>
              <a href="#governing-law" className="terms-toc-item">
                <span>10.</span> Governing Law
              </a>
              <a href="#changes" className="terms-toc-item">
                <span>11.</span> Changes to Terms
              </a>
              <a href="#contact" className="terms-toc-item">
                <span>12.</span> Contact Us
              </a>
            </div>
          </div>
        </section>

        {/* ============================= */}
        {/* TERMS CONTENT */}
        {/* ============================= */}
        <div className="terms-content-wrapper">
          {/* Section 1: Acceptance of Terms */}
          <section id="acceptance" className="terms-section">
            <div className="terms-section-number">01</div>
            <div className="terms-section-content">
              <h2>Acceptance of Terms</h2>
              <p>
                By accessing and using VernaEvents (the "Platform"), you agree to be bound by 
                these Terms of Service ("Terms"). If you do not agree to these Terms, please 
                do not use our Platform.
              </p>
              <div className="terms-highlight-box">
                <span>📌</span>
                <div>
                  <strong>Key Point:</strong>
                  <p>Using VernaEvents constitutes your acceptance of these Terms and our Privacy Policy.</p>
                </div>
              </div>
              <p>
                We reserve the right to update these Terms at any time. Your continued use of 
                the Platform after changes are posted constitutes your acceptance of the updated Terms.
              </p>
            </div>
          </section>

          {/* Section 2: User Accounts */}
          <section id="user-accounts" className="terms-section">
            <div className="terms-section-number">02</div>
            <div className="terms-section-content">
              <h2>User Accounts</h2>
              <p>
                To access certain features of VernaEvents, you may need to create an account. 
                You are responsible for maintaining the confidentiality of your account credentials.
              </p>
              <ul className="terms-list">
                <li>
                  <span className="terms-list-icon">🔐</span>
                  <div>
                    <strong>Account Security</strong>
                    <p>You are responsible for all activities that occur under your account</p>
                  </div>
                </li>
                <li>
                  <span className="terms-list-icon">📧</span>
                  <div>
                    <strong>Accurate Information</strong>
                    <p>You must provide accurate and complete information when creating your account</p>
                  </div>
                </li>
                <li>
                  <span className="terms-list-icon">🚫</span>
                  <div>
                    <strong>Prohibited Use</strong>
                    <p>You may not share your account credentials with others</p>
                  </div>
                </li>
                <li>
                  <span className="terms-list-icon">🛡️</span>
                  <div>
                    <strong>Account Termination</strong>
                    <p>We reserve the right to terminate accounts that violate these Terms</p>
                  </div>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 3: Event Registration */}
          <section id="event-registration" className="terms-section">
            <div className="terms-section-number">03</div>
            <div className="terms-section-content">
              <h2>Event Registration</h2>
              <p>
                When you register for an event through VernaEvents, you agree to the following:
              </p>
              <div className="terms-registration-grid">
                <div className="terms-registration-item">
                  <span className="terms-registration-icon">✅</span>
                  <div>
                    <h4>Team Eligibility</h4>
                    <p>Ensure your team meets all eligibility requirements for the event</p>
                  </div>
                </div>
                <div className="terms-registration-item">
                  <span className="terms-registration-icon">📝</span>
                  <div>
                    <h4>Accurate Details</h4>
                    <p>Provide accurate information about your team and players</p>
                  </div>
                </div>
                <div className="terms-registration-item">
                  <span className="terms-registration-icon">📅</span>
                  <div>
                    <h4>Event Rules</h4>
                    <p>Follow all rules and regulations set by the event organizers</p>
                  </div>
                </div>
                <div className="terms-registration-item">
                  <span className="terms-registration-icon">💰</span>
                  <div>
                    <h4>Fee Payment</h4>
                    <p>Complete all required payments within the specified timeframe</p>
                  </div>
                </div>
              </div>
              <p className="terms-section-note">
                <strong>Note:</strong> VernaEvents acts as a platform connecting teams with event organizers. 
                We are not responsible for the conduct of event organizers or participants.
              </p>
            </div>
          </section>

          {/* Section 4: Payments & Fees */}
          <section id="payments" className="terms-section">
            <div className="terms-section-number">04</div>
            <div className="terms-section-content">
              <h2>Payments & Fees</h2>
              <p>
                Certain events may require payment of registration fees. By registering for a paid event, 
                you agree to pay all applicable fees.
              </p>
              <div className="terms-payment-grid">
                <div className="terms-payment-item">
                  <span className="terms-payment-icon">💳</span>
                  <div>
                    <h4>Payment Methods</h4>
                    <p>We accept payments through secure payment gateways</p>
                  </div>
                </div>
                <div className="terms-payment-item">
                  <span className="terms-payment-icon">🔄</span>
                  <div>
                    <h4>Refund Policy</h4>
                    <p>Refunds are subject to the event organizer's cancellation policy</p>
                  </div>
                </div>
                <div className="terms-payment-item">
                  <span className="terms-payment-icon">💲</span>
                  <div>
                    <h4>Fee Changes</h4>
                    <p>Registration fees are displayed at the time of booking and may change</p>
                  </div>
                </div>
                <div className="terms-payment-item">
                  <span className="terms-payment-icon">🔒</span>
                  <div>
                    <h4>Secure Transactions</h4>
                    <p>All transactions are encrypted and processed securely</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Cancellation Policy */}
          <section id="cancellation" className="terms-section">
            <div className="terms-section-number">05</div>
            <div className="terms-section-content">
              <h2>Cancellation Policy</h2>
              <p>
                You may cancel your event registration subject to the following conditions:
              </p>
              <ul className="terms-list">
                <li>
                  <span className="terms-list-icon">⏰</span>
                  <div>
                    <strong>Cancellation Deadline</strong>
                    <p>Cancellations must be made at least 48 hours before the event</p>
                  </div>
                </li>
                <li>
                  <span className="terms-list-icon">💰</span>
                  <div>
                    <strong>Refund Eligibility</strong>
                    <p>Refunds are processed based on the event organizer's policy</p>
                  </div>
                </li>
                <li>
                  <span className="terms-list-icon">📧</span>
                  <div>
                    <strong>Notification</strong>
                    <p>All cancellations must be submitted through the platform or via email</p>
                  </div>
                </li>
                <li>
                  <span className="terms-list-icon">⚠️</span>
                  <div>
                    <strong>No-Show Policy</strong>
                    <p>Failure to attend without prior cancellation may result in forfeiture of fees</p>
                  </div>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 6: User Conduct */}
          <section id="user-conduct" className="terms-section">
            <div className="terms-section-number">06</div>
            <div className="terms-section-content">
              <h2>User Conduct</h2>
              <p>
                You agree to use VernaEvents responsibly and adhere to the following guidelines:
              </p>
              <div className="terms-conduct-grid">
                <div className="terms-conduct-item good">
                  <span className="terms-conduct-icon">✅</span>
                  <div>
                    <h4>Do's</h4>
                    <ul>
                      <li>Respect other users and event organizers</li>
                      <li>Provide accurate information</li>
                      <li>Follow event rules and guidelines</li>
                      <li>Report any issues to our support team</li>
                    </ul>
                  </div>
                </div>
                <div className="terms-conduct-item bad">
                  <span className="terms-conduct-icon">🚫</span>
                  <div>
                    <h4>Don'ts</h4>
                    <ul>
                      <li>Harass or abuse other users</li>
                      <li>Provide false or misleading information</li>
                      <li>Attempt to manipulate event registrations</li>
                      <li>Use the platform for unauthorized purposes</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 7: Intellectual Property */}
          <section id="intellectual-property" className="terms-section">
            <div className="terms-section-number">07</div>
            <div className="terms-section-content">
              <h2>Intellectual Property</h2>
              <p>
                All content on VernaEvents, including text, graphics, logos, and software, 
                is the property of VernaEvents and protected by intellectual property laws.
              </p>
              <ul className="terms-list">
                <li>
                  <span className="terms-list-icon">©️</span>
                  <div>
                    <strong>Copyright</strong>
                    <p>All content is protected by copyright laws</p>
                  </div>
                </li>
                <li>
                  <span className="terms-list-icon">™️</span>
                  <div>
                    <strong>Trademarks</strong>
                    <p>All trademarks and logos are the property of their respective owners</p>
                  </div>
                </li>
                <li>
                  <span className="terms-list-icon">🚫</span>
                  <div>
                    <strong>Restrictions</strong>
                    <p>You may not reproduce, distribute, or modify any content without permission</p>
                  </div>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 8: Disclaimer of Warranties */}
          <section id="disclaimer" className="terms-section">
            <div className="terms-section-number">08</div>
            <div className="terms-section-content">
              <h2>Disclaimer of Warranties</h2>
              <p>
                VernaEvents is provided "as is" and "as available" without any warranties of any kind.
              </p>
              <div className="terms-disclaimer-box">
                <span>⚠️</span>
                <div>
                  <strong>Important Disclaimer:</strong>
                  <p>
                    We do not guarantee that the platform will be uninterrupted, error-free, 
                    or free of viruses. You use the platform at your own risk. We are not responsible 
                    for any damages arising from your use of the platform.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 9: Limitation of Liability */}
          <section id="liability" className="terms-section">
            <div className="terms-section-number">09</div>
            <div className="terms-section-content">
              <h2>Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by law, VernaEvents shall not be liable for:
              </p>
              <ul className="terms-list">
                <li>
                  <span className="terms-list-icon">💔</span>
                  <div>
                    <strong>Direct Damages</strong>
                    <p>Any direct, indirect, or consequential damages arising from use of the platform</p>
                  </div>
                </li>
                <li>
                  <span className="terms-list-icon">📉</span>
                  <div>
                    <strong>Lost Profits</strong>
                    <p>Any loss of profits, revenue, or business opportunities</p>
                  </div>
                </li>
                <li>
                  <span className="terms-list-icon">🔒</span>
                  <div>
                    <strong>Third-Party Conduct</strong>
                    <p>Actions or conduct of event organizers or other users</p>
                  </div>
                </li>
                <li>
                  <span className="terms-list-icon">📱</span>
                  <div>
                    <strong>Technical Issues</strong>
                    <p>Technical failures, downtime, or data loss</p>
                  </div>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 10: Governing Law */}
          <section id="governing-law" className="terms-section">
            <div className="terms-section-number">10</div>
            <div className="terms-section-content">
              <h2>Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of India. 
                Any disputes arising from these Terms shall be subject to the exclusive jurisdiction 
                of the courts in Kolkata, West Bengal.
              </p>
              <div className="terms-law-box">
                <span>⚖️</span>
                <div>
                  <strong>Jurisdiction:</strong>
                  <p>Kolkata, West Bengal, India</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 11: Changes to Terms */}
          <section id="changes" className="terms-section">
            <div className="terms-section-number">11</div>
            <div className="terms-section-content">
              <h2>Changes to Terms</h2>
              <p>
                We may update these Terms from time to time. We will notify you of any changes 
                by posting the new Terms on this page and updating the "Last Updated" date.
              </p>
              <div className="terms-changes-timeline">
                <div className="terms-change-item">
                  <span className="terms-change-date">July 2026</span>
                  <span className="terms-change-version">Version 2.0</span>
                  <p>Major update with expanded terms and policies</p>
                </div>
                <div className="terms-change-item">
                  <span className="terms-change-date">January 2026</span>
                  <span className="terms-change-version">Version 1.0</span>
                  <p>Initial terms of service</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 12: Contact Us */}
          <section id="contact" className="terms-section">
            <div className="terms-section-number">12</div>
            <div className="terms-section-content">
              <h2>Contact Us</h2>
              <p>
                If you have any questions, concerns, or requests regarding these Terms of Service, 
                please contact us:
              </p>
              <div className="terms-contact-grid">
                <div className="terms-contact-item">
                  <span className="terms-contact-icon">📍</span>
                  <div>
                    <strong>Address</strong>
                    <p>VernaEvents, Justice Dwarakanath Rd, Gaza Park, Sreepally, Bhowanipore, Kolkata, West Bengal 700020</p>
                  </div>
                </div>
                <div className="terms-contact-item">
                  <span className="terms-contact-icon">📧</span>
                  <div>
                    <strong>Email</strong>
                    <p>info@vernaevents.com</p>
                  </div>
                </div>
                <div className="terms-contact-item">
                  <span className="terms-contact-icon">📱</span>
                  <div>
                    <strong>Phone</strong>
                    <p>+91 8017339796</p>
                  </div>
                </div>
                <div className="terms-contact-item">
                  <span className="terms-contact-icon">🕐</span>
                  <div>
                    <strong>Working Hours</strong>
                    <p>Mon - Sat: 9:00 AM - 6:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* ============================= */}
        {/* BOTTOM CTA */}
        {/* ============================= */}
        <section className="terms-bottom-cta">
          <div className="terms-bottom-content">
            <span className="terms-bottom-icon">📋</span>
            <div>
              <h4>Questions About Our Terms?</h4>
              <p>We're here to help clarify any questions about our terms of service</p>
            </div>
            <Link to="/contact" className="terms-bottom-btn">
              Contact Us →
            </Link>
          </div>
        </section>

       
       
      </div>
    </div>
  );
}

export default TermsOfService;